// /log — the Ledger. Desktop: week grid + inspector column. Mobile: one day
// with a week strip; editors open in the bottom sheet (root shell).
//
// `?date=YYYY-MM-DD&row=` deep-links a cell (legacy /day?date= redirects
// here); the params are consumed into the ledger selection and stripped.

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';
import { DayScreen } from '../components/ledger/DayScreen';
import { DesktopPage } from '../components/ledger/DesktopPage';
import { Ledger } from '../components/ledger/Ledger';
import { Sub } from '../components/ledger/ui';
import { C } from '../lib/design';
import { parseLocalDate } from '../lib/date';
import { LEDGER_ROWS, type LedgerRow } from '../lib/ledger';
import { useLedger } from '../lib/ledger-state';
import { useIsDesktop } from '../lib/useMediaQuery';

const searchSchema = z.object({
  date: z
    .string()
    .optional()
    .transform((d) => {
      if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return undefined;
      try {
        parseLocalDate(d);
        return d;
      } catch {
        return undefined;
      }
    }),
  row: z
    .string()
    .optional()
    .transform((r) => ((LEDGER_ROWS as readonly string[]).includes(r ?? '') ? (r as LedgerRow) : undefined)),
});

function LogRoute() {
  const desktop = useIsDesktop();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const ledger = useLedger();

  useEffect(() => {
    if (!search.date) return;
    ledger.select(search.date, search.row ?? 'day');
    navigate({ to: '/log', search: {}, replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.date, search.row]);

  if (!desktop) return <DayScreen />;
  return (
    <DesktopPage
      inspector="always"
      footer={
        <div style={{ padding: '16px 0 4px' }}>
          <Sub color={C.faint}>
            ← → ↑ ↓ MOVE · CLICK A DAY HEADER FOR ITS SUMMARY · EVERY CELL IS EDITABLE, PAST OR PRESENT
          </Sub>
        </div>
      }
    >
      <Ledger />
    </DesktopPage>
  );
}

export const Route = createFileRoute('/log')({
  component: LogRoute,
  validateSearch: searchSchema,
});
